<template>
  <section class="browse-games-wrapper">
    <img
      v-show="showChannelsView"
      class="game-list-bg"
      src="https://s3.amazonaws.com/images.growmindgrow/game-list/chanels-bg.jpg"
      alt=""
    >
    <img
      v-show="!showChannelsView"
      class="game-list-bg"
      src="https://s3.amazonaws.com/images.growmindgrow/game-list/game-list-bg.jpg"
      alt=""
    >

    <header class="head">
      <div class="view-switcher-wrapper">
        <div v-if="!mobileView" class="view-switcher">
          <img
            src="https://s3.amazonaws.com/images.growmindgrow/parent-site/assets/games/switch-view-icon.svg"
            alt="switch view icon"
            @click="setChannelsView(!showChannelsView)"
          >
        </div>
        <div class="filters">
          <div class="search-wrapper">
            <div
              v-if="mobileView"
              class="mobile-view-switcher"
              @click="setChannelsView(!showChannelsView)"
            >
              <img :src="require('@/assets/games/view-switcher.svg')" alt="">
            </div>
            <TextInput
              class="filters-item search"
              :value="selectedName"
              placeholder="Search"
              @typing="filterByName"
            />
          </div>
          <OptionList
            class="filters-item grade"
            :value="selectedGrades"
            :options="grades"
            placeholder="Grade"
            @selectOption="filterByGrade"
          />
          <OptionList
            class="filters-item category"
            :value="selectedCategories"
            :options="categories"
            placeholder="Category"
            @selectOption="filterByCategory"
          />
        </div>
      </div>
    </header>

    <div
      v-if="showChannelsView"
      class="games"
    >
      <div
        v-for="(item, index) in gamesGroupedByCategory"
        :key="item.category"
        class="games"
        @mouseover="setCursorBySwiper(index)"
        @mouseleave="setCursorBySwiper(null)"
      >
        <swiper
          ref="swipers"
          :options="swiperOptions"
          :auto-update="true"
          :auto-destroy="true"
          :delete-instance-on-destroy="false"
          :cleanup-styles-on-destroy="false"
          @slideChange="loadMoreSlides(item.category, item.categoryGames.length)"
        >
          <chanel-icon slot="button-prev" :chanel-name="item.category" />
          <swiper-slide
            v-for="game in item.categoryGames"
            :key="game._id"
            @click.native="goToGameView(game.id)"
          >
            <GameItem
              :game="game"
              :mastered-games="masteredGamesId"
            />
          </swiper-slide>
          <div class="button-prev" slot="button-prev" @click.stop="prevSlide(index)">
          </div>
          <div class="button-next" slot="button-next" @click.stop="nextSlide(index)">
          </div>
        </swiper>
      </div>
    </div>
    <div v-else class="search-view">
      <div class="games">
        <GameItem
          v-for="game in games"
          :key="game._id"
          :game="game"
          :mastered-games="masteredGamesId"
          @click.native="goToGameView(game.id)"
        />
      </div>
      <infinite-loading @infinite="loadGames" />
    </div>
  </section>
</template>

<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import { mapGetters, mapActions } from 'vuex';
import 'swiper/css/swiper.css';

function initialSwiperOptions() {
  return {
    slidesPerView: 5.5,
    spaceBetween: 33,
    freeMode: true,
    mousewheel: {
      forceToAxis: true,
      releaseOnEdges: true,
      invert: false,
    },
    navigation: {
      nextEl: '.button-next',
      prevEl: '.button-prev',
    },
    breakpoints: {
      2000: {
        slidesPerView: 8,
      },
      1500: {
        slidesPerView: 6,
      },
      1150: {
        slidesPerView: 5,
      },
      769: {
        slidesPerView: 4,
        spaceBetween: 33,
      },
      600: {
        slidesPerView: 3,
        spaceBetween: 8,
      },
      400: {
        slidesPerView: 3,
        spaceBetween: 8,
      },
      0: {
        slidesPerView: 2,
        spaceBetween: 8,
      },
    },
  };
}

export default {
  name: 'BrowseGames',
  components: {
    Swiper,
    SwiperSlide,
    GameItem: () => import('./GameItem'),
    ChanelIcon: () => import('@/components/icons/ChanelIcon'),
    TextInput: () => import('@/components/form-inputs/TextInput'),
    OptionList: () => import('@/components/form-inputs/OptionList'),
    InfiniteLoading: () => import('vue-infinite-loading'),
  },
  data() {
    return {
      currentCursorBySwiper: null,
      searchViewPage: 0,
      searchViewLimit: 20,
      fixedHeader: false,
      masteredGamesId: [],
      swiperLoadingStatuses: [],
      isLoading: false,
      swiperOptions: initialSwiperOptions(),
      mobileView: false,
    };
  },
  computed: {
    ...mapGetters({
      getGames: 'games/getGames',
      showChannelsView: 'games/getChannelsView',
      selectedName: 'games/getSelectedName',
      selectedCategories: 'games/getSelectedCategories',
      selectedGrades: 'games/getSelectedGrades',
    }),
    grades() {
      const games = this.getGames();
      const availableGrades = games.map(game => game.grade);
      let filteredGrades = [...new Set(availableGrades)];
      filteredGrades = filteredGrades.sort((a, b) => a - b);

      const result = filteredGrades.map((grade) => {
        let name;
        switch (grade) {
          case -1:
            name = 'Pre-K';
            break;
          case 0:
            name = 'K';
            break;
          default:
            name = grade;
        }
        const gamesByGrade = games.filter(game => game.grade === grade);
        return {
          name: `${name} (${gamesByGrade.length})`,
          value: name,
        };
      });

      return result;
    },
    categories() {
      const games = this.getGames();
      const allCategories = games.map(game => game.category);
      const uniqueCategories = [...new Set(allCategories)];
      const resultCategories = uniqueCategories.map((category) => {
        const gamesByCategory = games.filter(game => game.category === category);
        if (gamesByCategory && gamesByCategory.length) {
          return {
            name: `${category} (${gamesByCategory.length})`,
            value: category,
          };
        }
        return true;
      });

      return resultCategories;
    },
    games() {
      return this.getGames({
        name: this.selectedName,
        categories: this.selectedCategories,
        grades: this.selectedGrades,
      });
    },
    gamesGroupedByCategory() {
      const result = [];
      const categories = this.getGames().map(game => game.category);
      const uniqueCategoryRecords = [...new Set(categories)];

      uniqueCategoryRecords.forEach((category) => {
        const categoryGames = this.filterGames().filter(game => game.category === category);
        result.push({ category, categoryGames });
      });

      return result;
    },
  },
  watch: {
    selectedName(newVal) {
      this.setSelectedName(newVal || '');
      this.filterGames();
    },
  },
  mounted() {
    this.loadMoreSlides('', 0);

    const prevRoute = localStorage.getItem('prevRoute');
    if (prevRoute === 'parent-game-item') {
      const scrollY = localStorage.getItem('scrollPositionY');
      window.scrollTo(0, scrollY);
    }
    this.changeGameListView();
    window.addEventListener('resize', this.changeGameListView);
    window.addEventListener('scroll', this.handleHeaderPosition);
    window.addEventListener('keydown', this.keyDown);
  },
  methods: {
    ...mapActions({
      fetchGames: 'games/fetchGames',
      fetchGamesByCategory: 'games/fetchGamesByCategory',
      setChannelsView: 'games/setChannelsView',
      setSelectedName: 'games/setSelectedName',
      setSelectedCategories: 'games/setSelectedCategories',
      setSelectedGrades: 'games/setSelectedGrades',
    }),
    keyDown(e) {
      this.keyDownForSwiper(e.key);
    },
    keyDownForSwiper(key) {
      if (this.currentCursorBySwiper === null) return;

      const result = {
        ArrowLeft: () => this.prevSlide(this.currentCursorBySwiper),
        ArrowRight: () => this.nextSlide(this.currentCursorBySwiper),
      }[key];

      if (result) result();
    },
    setCursorBySwiper(i) {
      this.currentCursorBySwiper = i;
    },
    loadGames($state) {
      this.fetchGames({
        limit: this.searchViewLimit,
        page: this.searchViewPage,
      }).then((response) => {
        const { games } = response.data;
        if (games.length) {
          this.searchViewPage += 1;
          $state.loaded();
        } else {
          $state.complete();
        }
      });
    },
    loadMoreSlides(category, numberOfGames) {
      const status = this.swiperLoadingStatuses.find(item => item.value === category);
      if (this.isLoading || (status && status.loaded)) return;

      if (category) {
        this.swiperLoadingStatuses.push({
          value: category,
          loaded: false,
        });
      }

      const limit = 8;
      const skip = numberOfGames;
      this.isLoading = true;

      this.fetchGamesByCategory({
        skip,
        limit,
        category,
      }).then((response) => {
        if (!response.data.games.length) {
          const loadingStatus = this.swiperLoadingStatuses.find(item => item.value === category);
          loadingStatus.loaded = true;
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },
    changeGameListView() {
      const landscape = screen.orientation.angle || window.orientation;
      if (window.innerWidth < 769 || landscape) {
        this.mobileView = true;
      } else {
        this.mobileView = false;
      }
    },
    goToGameView(gameId) {
      if (this.mobileView) {
        localStorage.setItem('scrollPositionY', window.scrollY);
        this.$router.push({ name: 'game-view', params: { id: gameId } });
      }
    },
    filterByName(value) {
      this.setSelectedName(value);
      this.filterGames();
    },
    filterByCategory(values) {
      this.setSelectedCategories(values);
      this.filterGames();
    },
    filterByGrade(values) {
      this.setSelectedGrades(values);
      this.filterGames();
    },
    filterGames() {
      return this.getGames({
        name: this.selectedName,
        categories: this.selectedCategories,
        grades: this.selectedGrades,
      });
    },
    getSidesPerGroup() {
      let result = 5;
      const width = window.innerWidth;
      if (width <= 1150) {
        result = 3;
      } else if (width <= 1500) {
        result = 4;
      }
      return result;
    },
    swiper(i) {
      return this.$refs.swipers[i].$swiper;
    },
    prevSlide(i) {
      const shift = this.swiper(i).activeIndex - this.getSidesPerGroup();
      this.swiper(i).slideTo(shift > 0 ? shift : 0, 400, false);
    },
    nextSlide(i) {
      const shift = Math.abs(this.swiper(i).activeIndex + this.getSidesPerGroup() - 1);
      this.swiper(i).slideTo(shift, 400, false);
    },
    handleHeaderPosition() {
      this.fixedHeader = window.scrollY > 70;
    },
  },
  destroyed() {
    window.removeEventListener('resize', this.changeGameListView);
    window.removeEventListener('scroll', this.handleHeaderPosition);
    window.removeEventListener('keydown', this.keyDown);
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_breakpoints.scss";

.browse-games-wrapper {
  padding-top: 63px;

  @include for-size(small-tablets-only-or-landscape) {
    background: #F8F8F8;
    padding-top: 55px;
  }
  .game-list-bg {
    position: fixed;
    width: 100%;
    height: 100vh;
    top: 0;
    left: 0;
  }

  .view-switcher-wrapper {
    display: flex;
    margin-bottom: 16px;

    @include for-size(small-tablets-only-or-landscape) {
      margin-bottom: 16px;
    }
  }

  .view-switcher {
    display: flex;
    margin-right: 14px;
    margin-left: 8px;
    z-index: 1;
    background: #FFFFFF;
    border: 1px solid #D1D1D1;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 60px;
    margin: 0 8px;
    cursor: pointer;

      img {
        width: 50px;
        height: 50px;
      }
  }

  .filters {
    display: flex;
    width: 100%;

    @include for-size(small-tablets-only-or-landscape) {
      flex-wrap: wrap;
      justify-content: space-between;
      padding: 0 16px;
    }

    &-item {
      margin: 0 10px;
    }

    .search-wrapper {
      max-width: 350px;
      width: 100%;
      margin-right: 20px;
      @include for-size(small-tablets-only-or-landscape) {
        max-width: unset;
        flex-basis: 100%;
        display: flex;
        justify-content: space-between;
        margin-right: 0;
      }

      .mobile-view-switcher {
        min-width: 40px;
        width: 40px;
        height: 40px;
        background: #FFFFFF;
        box-shadow: -2px -2px 2px rgba(0, 0, 0, 0.05), 2px 2px 2px rgba(0, 0, 0, 0.05);
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        margin-right: 2%;
        z-index: 1;
      }

      .search {
        width: 100%;


        @include for-size(small-tablets-only-or-landscape) {
          max-width: unset;
          margin: 0 0 10px;
        }
      }
    }

    .category {
      max-width: 350px;
      width: 100%;

      @include for-size(small-tablets-only-or-landscape) {
        flex-basis: 49%;
        max-width: unset;
        margin: 0;
      }
    }
    .grade {
      max-width: 210px;
      width: 100%;

      @include for-size(small-tablets-only-or-landscape) {
        flex-basis: 49%;
        max-width: unset;
        margin: 0;
      }
    }
  }

  .games {
    display: flex;
    flex-wrap: wrap;
    overflow: hidden;
    padding-top: 12px;
    width: 100%;

    @include for-size(small-tablets-only-or-landscape) {
      padding-top: 0;
    }
    @include for-size(phone-only-or-landscape) {
      justify-content: space-between;
    }

    .swiper-container-free-mode ::v-deep .swiper-wrapper {
      &:hover {
        z-index: 2;
      }
    }

    .swiper-container {
      background: rgba(0, 0, 0, 0.2);
      padding: 60px 40px 14px;
      margin-bottom: 28px;
      min-width: 100%;
      overflow: visible;
      z-index: unset;

      @include for-size(small-tablets-only-or-landscape) {
        overflow: hidden;
        padding: 36px 16px 14px;
        margin-bottom: 26px;
      }

      .swiper-wrapper .swiper-slide {
          &:hover {
            z-index: 999;
          }

          ::v-deep .game-item {
            margin: 0 20px 0 0;

            @include for-size(small-tablets-only-or-landscape) {
              margin-right: 0;
            }

            .game-item-info-description {
              height: 78px;

              @include for-size(small-tablets-only-or-landscape) {
                height: auto;
              }

              span {
                min-height: 36px;
                height: 36px;
                display: -webkit-box;
                overflow: hidden;
                text-overflow: ellipsis;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                position: absolute;
                background: #fff;
                color: #000;
                border-bottom-left-radius: 16px;
                border-bottom-right-radius: 16px;
                left: 0;
                padding: 0 44px 0 16px;
              }
            }

            .mastered-game-icon {
              @include for-size(small-tablets-only-or-landscape) {
                right: 5px;
                top: 0;

                img {
                  @include for-size(small-tablets-only-or-landscape) {
                    width: 40px;
                  }
                }
              }
            }

            .premium-game-icon {
              top: 3px;
              left: -23px;
              width: 50px;
              @include for-size(small-tablets-only-or-landscape) {
                left: 5px;
                width: 40px;
              }

              img {
                width: 100%;
              }
            }

            .grade-icon {
              @include for-size(small-tablets-only-or-landscape) {
                display: none;
              }
            }

            .categoty-icon {
              @include for-size(small-tablets-only-or-landscape) {
                display: none;
              }
            }

            &:hover {
              transform: scale(1.1);
              transition: 0.25s;
              z-index: 2;
              @include for-size(small-tablets-only-or-landscape) {
                transform: none;
              }

              ::v-deep .game-item-info-description {
                p {
                  white-space: unset;

                  @include for-size(small-tablets-only-or-landscape) {
                    white-space: nowrap;
                  }
                }

                span {
                  height: auto;
                  display: block;
                  //box-shadow: 0 8px 5px -5px rgba(51, 51, 51, 0.12);
                  padding-bottom: 16px;
                }
              }
            }

            &.selected:hover {
              transform: none;
              transition: none;

              ::v-deep .game-item-info-description {
                span {
                  height: 36px;
                  overflow: hidden;
                  display: -webkit-box;
                  padding: 0 16px 0;
                  box-shadow: none;
                }
              }
            }
          }
        }
      }

      .category-label {
        position: absolute;
        left: 60px;
        top: -6px;

        @include for-size(small-tablets-only-or-landscape) {
          top: 5px;
          left: 16px;
        }
      }

      .button-next, .button-prev {
        height: 100%;
        right: 0;
        background-image: linear-gradient(90deg, rgba(2,0,36,0) 29%, rgba(0,0,0,1) 100%);
        border: none;
        width: 100px;

        display: flex;
        justify-content: center;
        align-items: center;
        //width: 38px;
        //height: 100%;
        //background: rgba(184, 184, 184, 0.85);
        border-radius: 5px;
        position: absolute;
        z-index: 3;
        top: 0;
        cursor: pointer;
        transition: 0.5s;

        @include for-size(small-tablets-only-or-landscape) {
          display: none
        }
      }

      .button-next {
        right: 0;
        &:after {
          content: '';
          border-style: solid;
          border-width: 20px 0 20px 10px;
          border-color: transparent transparent transparent #ffffff96;
          position: absolute;
          right: 20px;
        }
      }
      .button-prev {
        left: 0;
        transform: rotate(180deg);
        &:after {
          content: '';
          border-style: solid;
          border-width: 20px 0 20px 10px;
          border-color: transparent transparent transparent #ffffff96;
          position: absolute;
          right: 20px;
        }
      }
      .swiper-button-disabled {
        display: none;
      }
    }

    .game-item {
      margin: 0 60px 60px 0;

      @include for-size(small-tablets-only-or-landscape) {
        flex: 0 48%;
        margin: 0 20px 20px 0;
        height: auto;
        border-radius: 4px;
        overflow: hidden;
      }
      @include for-size(phone-only-or-landscape) {
        flex: 0 47%;
        margin-right: 0;
      }

      &:nth-child(even) {
        @include for-size(small-tablets-only-or-landscape) {
          margin-right: 0;
        }
      }
    }
  }

  .search-view {
    padding-left: 60px;

    @include for-size(small-tablets-only-or-landscape) {
      padding: 0 16px;
      justify-content: center;
    }
    @include for-size(phone-only-or-landscape) {
      padding: 0 16px;
      justify-content: space-between;
    }

    ::v-deep .game-item {
      height: auto;

      .mastered-game-icon {
        @include for-size(small-tablets-only-or-landscape) {
          right: 105px;
          top: 0;
        }
      }

      &-info-description {
        padding-right: 44px;
      }

      &-preview {
        img {
          height: auto;
          min-height: 100%;
          width: 100%;
          @include for-size(phone-only-or-landscape) {
            height: 100%;
            width: auto;
          }
        }
      }
    }
  }


.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
